import { ImageWithPlaceholder, Typography } from '@wbk/ui';
import { twMerge } from 'tailwind-merge';

import { Link } from 'react-router-dom';
import { ContentfulMedia } from '@wbk/contentful';
import { useTranslation } from '@wbk/config';
import type { ContentfulMedia as ContentfulMediaType } from '@wbk/contentful/api';

interface ZoneCardProps {
  buttonText: string;
  media: ContentfulMediaType;
  zoneLogoUrl?: string;
  className?: string;
  href?: string;
  onButtonClick?: () => void;
  presentedBy?: {
    name: string;
    logoUrl: string;
  };
  isNew: boolean;
}

const ZoneCard = ({
  media,
  href = '#',
  className,
  presentedBy,
  zoneLogoUrl,
  isNew = false,
}: ZoneCardProps) => {
  const Component = href ? Link : 'div';

  const { t } = useTranslation('common');

  return (
    <Component
      to={href}
      className={twMerge(
        'group relative block w-full overflow-hidden rounded-2xl md:rounded-3xl',
        className
      )}
    >
      <div className='relative flex h-[128px] flex-col sm:h-80'>
        <ContentfulMedia
          className='h-full w-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-110'
          media={media}
        />
        <div className='absolute inset-0 bg-black/60 opacity-60 blur-[3.2px] transition-all duration-300 ease-in-out group-hover:opacity-30' />{' '}
        <div className='absolute inset-0 bottom-[0px] end-[0px] top-auto z-20 flex h-fit flex-row items-end justify-between gap-2 p-2 sm:bottom-[8px] sm:end-[8px]'>
          <div
            style={{
              visibility: presentedBy?.name ? 'visible' : 'hidden',
            }}
            className='flex size-[30px] w-auto items-center justify-center gap-2 !rounded-2xl bg-black/80 p-2 text-[13px] text-xs font-semibold backdrop-blur-[1px] transition-colors sm:size-[40px] sm:w-full sm:justify-between sm:text-[15px] lg:size-full xl:bg-[#0000009d] xl:p-4'
          >
            {presentedBy?.logoUrl && (
              <>
                <p className='text-nowrap text-[8px] sm:text-[1.2vw] lg:text-[0.6vw] xl:text-[0.7vw] 2xl:text-[13px]'>
                  {t('presented_by')}
                </p>
                <ImageWithPlaceholder
                  src={presentedBy?.logoUrl}
                  alt={presentedBy?.name}
                  className='size-[8vw] max-h-[14px] object-contain sm:size-[4vw] sm:max-h-max lg:size-[2vw] xl:w-8'
                  draggable={false}
                />
              </>
            )}
          </div>
          {zoneLogoUrl && (
            <div className=''>
              <div className='flex size-[40px] items-center justify-center rounded-[4px] bg-black/80 p-[8px] backdrop-blur-sm sm:size-[96px] sm:rounded-[16px]'>
                <ImageWithPlaceholder
                  src={zoneLogoUrl}
                  alt={zoneLogoUrl}
                  className='size-[25px] object-contain sm:size-[64px]'
                  draggable={false}
                />
              </div>
            </div>
          )}
        </div>
        {isNew && (
          <span className='absolute left-[1.11vw] top-[1.11vw] rounded-full border border-[#32C200] bg-[#32C20033] px-[2.22vw] py-[0.55vw]'>
            <Typography className='text-body-XS sm:text-body-S'>{t('new')}</Typography>
          </span>
        )}
      </div>
    </Component>
  );
};

export default ZoneCard;
